import { Fragment, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form, Select, DatePicker, Radio, message, Spin, Popover, Checkbox } from 'antd';
import { FilterFilled } from '@ant-design/icons';
import locale from 'antd/es/date-picker/locale/es_ES';
import moment from 'moment-timezone';
import 'moment/locale/es';
import ForecastList from './components/ForecastList';
import useForecasts from './hooks/useForecasts';
import { getAllBlocks } from '../../services/reservation';
import { handlers } from '../../helpers/helpers';

const ReservationForecastPage = () => {
	const [queryParams, setQueryParams] = useState(() => {
		const query = Object.fromEntries(new URLSearchParams(window.location.search));
		return query;
	});
	const forecastData = useForecasts(null, queryParams);
	const { t } = useTranslation();
	const { Option } = Select;
	const { RangePicker } = DatePicker;
	const [blocks, setBlocks] = useState([])
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const alignment = {
		display: "flex",
		alignItems: "center",
	};
	const [isOpen, setIsOpen] = useState(false);
	const patientStates = ['null_state', 'not_contacted', 'will_attend', 'will_not_attend'];
	const [selectedPatientStates, setSelectedPatientStates] = useState(patientStates);
	const selectAllPatients = patientStates.length === selectedPatientStates.length;
	const patientsIndeterminate = selectedPatientStates.length > 0 && selectedPatientStates.length < patientStates.length;
	const forecastStates = ['nula', 'baja', 'media', 'alta'];
	const [selectedForecastStates, setSelectedForecastStates] = useState(forecastStates);
	const selectAllForecasts = forecastStates.length === selectedForecastStates.length;
	const forecastIndeterminate = forecastStates.length > 0 && selectedForecastStates.length < forecastStates.length;
	
  const updateAndSetParams = (queryString) => {
		const paramValues = Object.fromEntries(
			Object.entries(queryString).filter(([_, values]) => values || values === "")
		);

		const paramValuesURL = Object.fromEntries(
			Object.entries(queryString).filter(([key, value]) => {
				return (value || value === "") && key !== "assistance_state" && key !== "predict_category";
			})
		)
		updateSearchParams(paramValues);
		setQueryParams(paramValues);
		handlers.replaceUrlQueryString(navigate, paramValuesURL)
	}

	const handleDateChange = (dates) => {
		const queryString = {
			startDate: dates && moment(dates[0], 'YYYY-MM-DD').format('YYYY-MM-DD'),
			endDate: dates && moment(dates[1], 'YYYY-MM-DD').format('YYYY-MM-DD'),
			orderValue: forecastData.params.orderValue,
			orderRule: forecastData.params.orderRule,
			assistance_state: selectedPatientStates.length === 4 ? 'all' : selectedPatientStates,
			predict_category: selectedForecastStates.length === 4 ? 'all' : selectedForecastStates
		};

		updateAndSetParams(queryString);
	};

	const handleSortByAttendance = (value) => {
		const queryString = {
			orderValue: 'predict_rf',
			orderRule: value,
			startDate: forecastData.params.startDate,
			endDate: forecastData.params.endDate,
			assistance_state: selectedPatientStates.length === 4 ? 'all' : selectedPatientStates,
			predict_category: selectedForecastStates.length === 4 ? 'all' : selectedForecastStates
		};

		updateAndSetParams(queryString);
	}

	const handlePatientStateChange = (state) => {
		setSelectedPatientStates(state);
		updateQueryString({ assistance_state: state });
	}

	const handleSelectAllPatientChange = (state) => {
		if (state.target.checked) {
			setSelectedPatientStates(patientStates)
			updateQueryString({ assistance_state: 'all' })
		} else {
			setSelectedPatientStates([]);
			updateQueryString({ assistance_state: '' });
		}
	}

	const handleForecastStateChange = (state) => {
		setSelectedForecastStates(state);
		updateQueryString({ predict_category: state });
	}

	const handleSelectAllForecastChange = (state) => {
		if (state.target.checked) {
			setSelectedForecastStates(forecastStates)
			updateQueryString( { predict_category: 'all'})
		} else {
			setSelectedForecastStates([]);
			updateQueryString({ predict_category: '' })
		}
	}

	const updateQueryString = ( { predict_category, assistance_state }) => {
		const queryString = {
			assistance_state: assistance_state ?? (selectedPatientStates.length === 4 ? 'all' : selectedPatientStates),
			predict_category: predict_category ?? (selectedForecastStates.length === 4 ? 'all' : selectedForecastStates),
			orderValue: forecastData.params.orderValue,
			orderRule: forecastData.params.orderRule,
			startDate: forecastData.params.startDate,
			endDate: forecastData.params.endDate
		}
		updateAndSetParams(queryString)
	}

	const fetchAllBlocks = async () => {
		try {
			const response = await getAllBlocks();
			setBlocks(response)
		} catch (err) {
			message.error("Error")
			console.error(err)
		}
	}

	forecastData.data = forecastData.data && blocks.length > 0 &&
		forecastData.data.map((value) => {
			return {
				...value,
				prestations: value?.prestations?.map((service) => {
					const matchingBlocks = blocks.find((block) => block.id === service.prestation_block)

					if (matchingBlocks) {
						return { ...service, blockTime: matchingBlocks.start }
					} else {
						return { ...service }
					}
				})
			}
		})

	const disabledDate = current => {
		return current && current < moment().startOf('day');
	};

	const updateSearchParams = (params) => {
		forecastData.setParams(params);
		forecastData.changePage(1);
	}

	const loadMore = (event) => {
		const direction = event.target.value;
		const _currentPage =
			direction === "next"
				? forecastData.currentPage + 1
				: forecastData.currentPage - 1;
		forecastData.changePage(_currentPage);
	};

	useEffect(() => {
		updateSearchParams(queryParams)
	}, [queryParams])

	useEffect(() => {
		const formParams = {
			dates: queryParams.startDate && [moment(queryParams.startDate), moment(queryParams.endDate)],
			order: queryParams?.orderRule,
		};
		form.setFieldsValue(formParams);
	}, []);

	useEffect(() => {
		fetchAllBlocks();
	}, [])

	return (
		<Row justify="center" className="reservation-page-wrapper">
			<Col lg={24} xs={24}>
				<Row justify="center">
					<Col lg={24} xs={24} className="reservation-page-wrapper__header forecast-header pt-20">
						<Row justify="center">
							<Col lg={22} xs={22}>
								<Row>
									<Col className="back-button-wrapper">
										<Link to="/reservas/buscar">
											<i className="icon-system-chevron-left" />
											Volver
										</Link>
									</Col>
									<Col lg={24} xs={24}>
										<h1 className="text-center">
											{t('reservations.forecast.title')}
										</h1>
										<h3 className="forecast-subtitle text-center mb-20">
											{t('reservations.forecast.sub-title')}
										</h3>
									</Col>
								</Row>
							</Col>
						</Row>
					</Col>
				</Row>
				<Col lg={24} xs={24} className="reservation-page-wrapper__body forecast-container mt-20">
					<Row justify="center">
						<Col lg={22} xs={22}>
							<Form
								form={form}
								size="large"
								layout="vertical"
								className='cmt-form'
							>
								<Row className="form-wrapper">
									<Col xxl={12} lg={12} xs={24} className="mt-10">
										<p>{t('reservations.forecast.attendanceProbabilityTitle')}</p>
										<Row gutter={20} className="symbols-wrapper mt-30 mb-10">
											<Col xxl={3} lg={4} xs={6} style={alignment}>
												<div className="red-state mr-10" />
												<p className='mb-0'>{t('reservations.forecast.nullTab')}</p>
											</Col>
											<Col xxl={3} lg={4} xs={6} style={alignment}>
												<div className="orange-state mr-10" />
												<p className='mb-0'>{t('reservations.forecast.lowTab')}</p>
											</Col>
											<Col xxl={3} lg={4} xs={6} style={alignment}>
												<div className="yellow-state mr-10" />
												<p className='mb-0'>{t('reservations.forecast.midTab')}</p>
											</Col>
											<Col xxl={3} lg={4} xs={6} style={alignment}>
												<div className="green-state mr-10" />
												<p className='mb-0'>{t('reservations.forecast.highTab')}</p>
											</Col>
										</Row>
									</Col>
									<Col xxl={6} lg={6} xs={24} className="mr-20">
										<Form.Item name="dates" label="Fecha">
											<RangePicker
												className="cmt-range-picker"
												onChange={handleDateChange}
												disabledDate={disabledDate}
												locale={locale}
												inputReadOnly={true}
												placeholder={['Desde', 'Hasta']}
												dropdownClassName="custom-dropdown"
												style={{ width: '100%' }}
											/>
										</Form.Item>
									</Col>
									<Col xxl={5} lg={5} xs={24}>
										<Form.Item name="order" label="Asistencia">
											<Select
												className="cmt-select"
												placeholder="Ordenar por"
												onChange={handleSortByAttendance}
											>
												<Option value="asc">{t('reservations.forecast.lowestToHighestDropdown')}</Option>
												<Option value="desc">{t('reservations.forecast.highestToLowestDropdown')}</Option>
											</Select>
										</Form.Item>
									</Col>
								</Row>
							</Form>
						</Col>
						<Fragment>
							<Col
								lg={22}
								xs={22}
								className="draft-wrapper mt-lg-30 mt-md-20 mt-sm-20 mt-xs-20 mb-lg-30 mb-md-20 mb-sm-20 mb-xs-20"
							>
								<Row justify="space-between" className="table-header">
									<Col lg={3}>
										<p className="mb-5 ml-15">
											{t('reservations.forecast.reservationNumber')}
										</p>
									</Col>
									<Col lg={3}>
										<p className="text-center mb-5">
											{t('reservations.forecast.userTitle')}
										</p>
									</Col>
									<Col lg={2}>
										<p className="text-center mb-5">
											{t('reservations.forecast.branchOfficeTitle')}
										</p>
									</Col>
									<Col lg={3}>
										<p className="text-center mb-5">
											{t('reservations.forecast.servicesTitle')}
										</p>
									</Col>
									<Col lg={3}>
										<p className="text-center mb-5">
											{t("reservations.forecast.dateTitle")}
										</p>
									</Col>
									<Col lg={3}>
										<p className="text-center mb-5">
											{t("reservations.forecast.telephoneTitle")}
										</p>
									</Col>
									<Col lg={3}>
                    <p className="text-center mb-5">
											{t("reservations.forecast.patientState")}
                      <Popover
                        trigger="hover"
                        overlayClassName="forecast-tooltip"
                        placement="bottomRight"
												content={ isOpen && 
													<>
													<Checkbox indeterminate={patientsIndeterminate} onChange={handleSelectAllPatientChange} checked={selectAllPatients} className="cmt-checkbox">
														{t('reservations.forecast.selectAll')}
													</Checkbox>
													<Checkbox.Group value={selectedPatientStates} onChange={handlePatientStateChange}>
                           	<Checkbox value="null_state" className="cmt-checkbox ml-20">{t('reservations.forecast.empty')}</Checkbox><br />
                            <Checkbox value="not_contacted" className="cmt-checkbox ml-20">{t('reservations.forecast.notContacted')}</Checkbox><br />
                            <Checkbox value="will_attend" className="cmt-checkbox ml-20">{t('reservations.forecast.willAttend')}</Checkbox><br />
                            <Checkbox value="will_not_attend" className="cmt-checkbox ml-20">{t('reservations.forecast.willNotAttend')}</Checkbox><br />
                          </Checkbox.Group>
													</>
                        }
                        onClick = {() => setIsOpen(!isOpen) } 
                      >
                        <FilterFilled className="ml-5" style={{ display: 'none' }}/>
                      </Popover>
                    </p>
									</Col>
									<Col lg={2}>
										<p className="text-center mb-5">
											{t("reservations.forecast.attendanceTitle")}
											<Popover
                        trigger="hover"
                        overlayClassName="forecast-tooltip"
                        placement="bottomRight"
												content={ isOpen && 
													<>
														<Checkbox indeterminate={forecastIndeterminate} onChange={handleSelectAllForecastChange} checked={selectAllForecasts} className="cmt-checkbox">
															{t('reservations.forecast.selectAll')}
														</Checkbox>
														<Checkbox.Group value={selectedForecastStates} onChange={handleForecastStateChange}>
															<Checkbox value="nula" className="cmt-checkbox ml-20"><Row className="symbols-key"><div className="red-state-sm"></div>&nbsp;{t('reservations.forecast.nullFilter')}</Row></Checkbox><br />
															<Checkbox value="baja" className="cmt-checkbox ml-20"><Row className="symbols-key"><div className="orange-state-sm"></div>&nbsp;{t('reservations.forecast.lowFilter')}</Row></Checkbox><br />
															<Checkbox value="media" className="cmt-checkbox ml-20"><Row className="symbols-key"><div className="yellow-state-sm"></div>&nbsp;{t('reservations.forecast.midFilter')}</Row></Checkbox><br />
															<Checkbox value="alta" className="cmt-checkbox ml-20"><Row className="symbols-key"><div className="green-state-sm"></div>&nbsp;{t('reservations.forecast.highFilter')}</Row></Checkbox><br />
														</Checkbox.Group>
													</>
                        }
                        onClick = {() => setIsOpen(!isOpen) } 
                      >
                        <FilterFilled className="ml-5"
													style={{ display: 'none' }} />
                      </Popover>
										</p>
									</Col>
									<Col lg={2} xs={3}>
									</Col>
								</Row>
								<Spin className="cmt-spin" spinning={forecastData.isLoading}>
									{forecastData?.data && forecastData.data.map((forecast, index) => {
										return (
											<ForecastList
												forecastData={forecastData}
												forecast={forecast}
												index={index}
											>
											</ForecastList>
										)
									})}
								</Spin>
							</Col>
							<Col lg={22}>
								{forecastData && forecastData.pagination && (
									<Row className="mb-50">
										<Col>
											<Radio.Group className="cmt-pagination">
												<Radio.Button
													value="prev"
													disabled={forecastData.currentPage === 1}
													onClick={loadMore}
												>
													Anterior
												</Radio.Button>
												<Radio.Button
													value="next"
													disabled={
														forecastData.data?.length === 0 ||
														forecastData.pagination.lastPage === forecastData.currentPage
													}
													onClick={loadMore}
												>
													Siguiente
												</Radio.Button>
											</Radio.Group>
										</Col>
										<Col
											className="ml-20"
											style={{
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
											}}
										/>
									</Row>
								)}
							</Col>
						</Fragment>
					</Row>
				</Col>
			</Col>
		</Row>
	)
}

export default ReservationForecastPage;
